import { z } from "zod";

import { US_STATE_CODES } from "./usStates.js";

/** Per-field ceiling, the same one boxed enforced on its `ShippingAddressInput`. */
export const SHIPPING_ADDRESS_FIELD_MAX = 50;

const requiredField = z.string().trim().min(1).max(SHIPPING_ADDRESS_FIELD_MAX);
const optionalField = z.string().trim().max(SHIPPING_ADDRESS_FIELD_MAX).optional();

/**
 * An end user's shipping address (boxed parity: `ShippingAddressInput`, the same nine fields):
 * the shopper's own, entered on the storefront's account page through `PUT /v1/me/shipping-address`,
 * or recorded by staff from the dashboard, stored on `end_users.shipping_address`. `country` is an
 * ISO 3166-1 alpha-2 code, the form the shipping config and the KYC country list use; boxed took a
 * country name and derived the code. An order copies its own address at creation and never reads
 * this one again.
 */
export const shippingAddressSchema = z.object({
  firstName: requiredField,
  lastName: requiredField,
  addressLine1: requiredField,
  addressLine2: optionalField,
  locality: requiredField,
  region: requiredField,
  postalCode: requiredField,
  country: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z]{2}$/, "two-letter ISO country code"),
  phoneNumber: optionalField,
});
export type ShippingAddress = z.infer<typeof shippingAddressSchema>;

/** The fields in the order a form shows them, with their labels. */
export const SHIPPING_ADDRESS_FIELDS: readonly {
  key: keyof ShippingAddress;
  label: string;
  required: boolean;
}[] = [
  { key: "firstName", label: "First name", required: true },
  { key: "lastName", label: "Last name", required: true },
  { key: "addressLine1", label: "Address line 1", required: true },
  { key: "addressLine2", label: "Address line 2", required: false },
  { key: "locality", label: "City", required: true },
  { key: "region", label: "State or region", required: true },
  { key: "postalCode", label: "Postal code", required: true },
  { key: "country", label: "Country", required: true },
  { key: "phoneNumber", label: "Phone number", required: false },
];

/**
 * Postal-code formats for the three countries boxed checks, and deliberately no others: the
 * check is an anti-abuse control on the countries with the most traffic, not a world gazetteer.
 * A country absent here passes any postal code.
 */
export const POSTAL_CODE_PATTERNS: Readonly<Record<string, RegExp>> = {
  US: /^\d{5}(-\d{4})?$/,
  CA: /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/,
  GB: /^[A-Z]{1,2}\d[A-Z\d]? ?\d[A-Z]{2}$/i,
};

/** Whether `postalCode` fits `countryCode`'s format; true for a country with no pattern. */
export function validatePostalCode(postalCode: string, countryCode: string): boolean {
  const pattern = POSTAL_CODE_PATTERNS[countryCode.toUpperCase()];
  return pattern ? pattern.test(postalCode.trim()) : true;
}

/**
 * Why an address that parses is still refused, one member per cause so a message and a form
 * field can be picked from it: the country is not an enabled shipping destination, the postal
 * code does not fit the country, or a US region is not a state or territory code.
 */
export const SHIPPING_ADDRESS_ISSUES = [
  "unsupported_destination",
  "postal_code_mismatch",
  "invalid_region",
] as const;
export type ShippingAddressIssue = (typeof SHIPPING_ADDRESS_ISSUES)[number];

/** The field-level issue with a parsed address, or null. Pure: the form and the server both run it. */
export function shippingAddressFieldIssue(
  address: Pick<ShippingAddress, "country" | "region" | "postalCode">,
): Exclude<ShippingAddressIssue, "unsupported_destination"> | null {
  const country = address.country.toUpperCase();
  if (country === "US" && !US_STATE_CODES.has(address.region.trim().toUpperCase())) {
    return "invalid_region";
  }
  if (!validatePostalCode(address.postalCode, country)) return "postal_code_mismatch";
  return null;
}

/**
 * What address validation said about an address: Shippo's three verdicts, or `unverified` when
 * this stage has no validator or it did not answer, in which case the save proceeds on the field
 * rules alone.
 */
export const ADDRESS_VALIDATION_STATUSES = [
  "valid",
  "invalid",
  "partially_valid",
  "unverified",
] as const;
export type AddressValidationStatus = (typeof ADDRESS_VALIDATION_STATUSES)[number];
export const addressValidationStatusSchema = z.enum(ADDRESS_VALIDATION_STATUSES);

/**
 * POST /v1/me/shipping-address/validate. `correctedAddress` is the entered address with the
 * validator's recommended fields laid over it, offered when the verdict is `partially_valid`;
 * the shopper chooses it or keeps their own.
 */
export const shippingAddressValidationSchema = z.object({
  status: addressValidationStatusSchema,
  correctedAddress: shippingAddressSchema.optional(),
});
export type ShippingAddressValidation = z.infer<typeof shippingAddressValidationSchema>;

/** GET and PUT /v1/me/shipping-address. */
export const shippingAddressResponseSchema = z.object({
  shippingAddress: shippingAddressSchema.nullable(),
});
export type ShippingAddressResponse = z.infer<typeof shippingAddressResponseSchema>;
